import { memo, useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tree } from 'react-arborist';
import type { NodeRendererProps } from 'react-arborist';
import { ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import type { NodeTreeItem } from '@/stores/scope';

type Props = {
  /** 選択候補のノードツリー (StoreMultiSelectと異なり店舗以外の任意ノードも選択対象にする、
   *  v0.3 role_permissions.node_ids はブランド等の祖先ノードも直接指定できるため) */
  nodes: NodeTreeItem[];
  selectedIds: Set<string>;
  onChange: (next: Set<string>) => void;
  /** v0.4 TD-22: 読み取り専用表示。トリガーを無効化してパネル自体を開けなくすることで
   *  「編集できたように見えて保存できない」状態を防ぐ (design-expert Critical)。 */
  disabled?: boolean;
};

const normalize = (s: string) => s.toLowerCase().replace(/[\s　]+/g, '');
const matchesQuery = (node: NodeTreeItem, query: string) =>
  normalize(node.name).includes(query) || normalize(node.node_code).includes(query);

const collectAllIds = (nodes: NodeTreeItem[]): string[] =>
  nodes.flatMap((n) => [n.node_id, ...collectAllIds(n.children)]);

type NodeMultiSelectRowProps = {
  node: NodeRendererProps<NodeTreeItem>['node'];
  style: NodeRendererProps<NodeTreeItem>['style'];
  isSelected: boolean;
  onToggle: (node: NodeTreeItem) => void;
};

/** モジュールスコープで1度だけ定義し React.memo でラップする (StoreMultiSelect/NodeSwitcher と
 *  同型の不具合の横展開修正、2026-08-11)。内部定義のままだと再レンダーのたびに別コンポーネント
 *  扱いになり、react-arborist が可視行を毎回アンマウント/リマウントしてしまう。 */
const NodeMultiSelectRow = memo(function NodeMultiSelectRow({ node, style, isSelected, onToggle }: NodeMultiSelectRowProps) {
  return (
    <div
      style={style}
      className="flex items-center gap-1.5 px-1 rounded hover:bg-neutral-panel cursor-pointer"
      onClick={() => onToggle(node.data)}
    >
      {node.isInternal ? (
        <button type="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); node.toggle(); }}
          className="flex items-center justify-center w-[18px] shrink-0 text-neutral-sub" aria-label={node.isOpen ? '折り畳む' : '開く'}>
          {node.isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
      ) : <span className="w-[18px] shrink-0" />}
      <input
        type="checkbox"
        tabIndex={-1}
        checked={isSelected}
        onChange={() => onToggle(node.data)}
        className="w-4 h-4 accent-brand-red cursor-pointer"
        aria-label={`${node.data.name} を選択`}
      />
      <span className="truncate text-sm">{node.data.name}</span>
      <span className="text-[10px] text-neutral-sub ml-auto shrink-0 pl-2">{node.data.node_type_code}</span>
    </div>
  );
});

/** 権限のnode_scope選択用の軽量ツリー選択UI (v0.3 S-16)。
 *  StoreMultiSelect (v0.2.5) と同じ react-arborist ベースのパネルパターンを踏襲しつつ、
 *  「店舗ノードのみ」の枝刈り・保存済みビューは持たない (どのノード種別も直接選択対象になるため)。 */
export function NodeMultiSelect({ nodes, selectedIds, onChange, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  // ⚠️ bugfix (2026-09-09、本番実機報告「ノード選択の選択肢が画面下端で埋もれて選べない」で発覚):
  // 従来は `absolute` + `top-full` でトリガー直下に固定配置していたため、祖先要素の
  // `overflow-hidden` (RolePermissionsEditPage.tsx の機能行カード等) にクリップされ、かつ画面下端
  // との衝突判定も無かった。Portal で document.body 直下に描画し、トリガー位置から動的に
  // 上下どちらに開くか・利用可能な高さを計算する (Radix Popover 相当の挙動を最小実装)。
  const [panelPos, setPanelPos] = useState<{ left: number; maxHeight: number; top: number | null; bottom: number | null } | null>(null);

  const allIds = useMemo(() => collectAllIds(nodes), [nodes]);

  const recomputePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const margin = 8;
    const desiredHeight = 420; // 検索欄+ツリー(280)+フッター分の目安
    const spaceBelow = viewportHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const openUpward = spaceBelow < 240 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(200, Math.min(desiredHeight, openUpward ? spaceAbove : spaceBelow));
    setPanelPos({
      left: Math.min(rect.left, window.innerWidth - 320 - margin),
      maxHeight,
      top: openUpward ? null : rect.bottom + margin,
      bottom: openUpward ? viewportHeight - rect.top + margin : null,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) { setPanelPos(null); return; }
    recomputePosition();
    const onReposition = () => recomputePosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, recomputePosition]);

  useEffect(() => {
    if (!open) { setQuery(''); return; }
    const handle = setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => clearTimeout(handle);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node) || triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>('button:not(:disabled), input, [tabindex]:not([tabindex="-1"])'),
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      triggerRef.current?.focus();
    };
  }, [open]);

  const toggleNode = useCallback((node: NodeTreeItem) => {
    const next = new Set(selectedIds);
    if (next.has(node.node_id)) next.delete(node.node_id);
    else next.add(node.node_id);
    onChange(next);
  }, [selectedIds, onChange]);

  const renderRow = useCallback(
    ({ node, style }: NodeRendererProps<NodeTreeItem>) => (
      <NodeMultiSelectRow node={node} style={style} isSelected={selectedIds.has(node.data.node_id)} onToggle={toggleNode} />
    ),
    [selectedIds, toggleNode],
  );

  // 検索1文字ごとの再走査で入力がカクつかないよう、絞り込み計算は低優先度に遅延させる
  // (React 18 useDeferredValue、StoreMultiSelect と同一パターン、2026-08-11)
  const deferredQuery = useDeferredValue(query);

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 px-2 py-1 min-h-[44px] rounded border border-neutral-line bg-white text-xs hover:bg-neutral-panel focus-ring disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-white"
      >
        ノード選択 <span className="tabular-nums text-brand-red font-semibold">({selectedIds.size})</span>
        <ChevronDown size={12} className="text-neutral-sub" />
      </button>

      {open && panelPos && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="ノード選択パネル"
          style={{
            position: 'fixed', left: panelPos.left,
            top: panelPos.top ?? undefined, bottom: panelPos.bottom ?? undefined,
            maxHeight: panelPos.maxHeight,
          }}
          className="z-[60] w-[320px] max-w-[calc(100vw-2rem)] flex flex-col rounded-lg border border-neutral-line bg-white shadow-lg overflow-hidden"
        >
          <div className="border-b border-neutral-line px-3 py-2 flex items-center gap-2">
            <Search size={14} className="text-neutral-sub flex-shrink-0" />
            <input
              ref={searchInputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); setOpen(false); } }}
              placeholder="ノード名・コードで検索"
              className="flex-1 text-sm bg-transparent focus:outline-none placeholder:text-neutral-sub"
              aria-label="ノード検索"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="text-neutral-sub hover:text-brand-red" aria-label="検索クエリをクリア">
                <X size={14} />
              </button>
            )}
          </div>
          <div className="px-1 py-1 overflow-y-auto">
            {nodes.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-neutral-sub">ノードがありません</div>
            ) : (
              <Tree<NodeTreeItem>
                data={nodes}
                idAccessor="node_id"
                childrenAccessor="children"
                openByDefault={false}
                searchTerm={deferredQuery}
                searchMatch={(node, term) => matchesQuery(node.data, normalize(term))}
                width="100%"
                height={Math.max(120, panelPos.maxHeight - 92)}
                indent={16}
                rowHeight={32}
                onActivate={(node) => toggleNode(node.data)}
              >
                {renderRow}
              </Tree>
            )}
          </div>
          <div className="border-t border-neutral-line px-3 py-2 flex items-center justify-between text-xs bg-neutral-panel">
            <span className="text-neutral-sub tabular-nums">全 {allIds.length} ノード中 {selectedIds.size} 件選択</span>
            <button type="button" onClick={() => onChange(new Set())} className="text-neutral-sub hover:text-brand-red">
              全解除
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
